/**
 * AnalyticsProvider: inits PostHog (pixels + feature flags) and Mixpanel (events).
 * Events are sent to both when configured; PostHog is also used for feature flags (server + client).
 * Renders AnalyticsIdentify so identify/reset run when auth state changes.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Platform } from 'react-native'
import { LAUNCH_PHASE } from '../shared/constants/launchDiagnostics.constants'
import { logLaunchPhase } from '../shared/helpers/launchDiagnostics.helper'
import {
  IOS_DISABLED_NATIVE_SDKS,
  NATIVE_SDK_MIXPANEL_STAGGER_MS,
  NATIVE_SDK_POSTHOG_STAGGER_MS,
} from '../shared/constants/nativeSdkInit.constants'
import { useNativeSdkInit } from '../shared/contexts/NativeSdkInitContext'
import { logger } from '../helpers/logger.helper'
import { useSupabaseAuth } from '../shared/contexts/SupabaseAuthContext'
import { initMixpanel } from './index'
import { useAnalytics } from './useAnalytics'
import { getAnalyticsEnvironment } from './environment.helper'
import { Mixpanel } from './mixpanelPlatform'
import { PostHogClientContext } from './PostHogClientContext'
import {
  getWebAnalyticsConsent,
  hydrateNativeAnalyticsConsent,
  subscribeAnalyticsConsent,
} from '../privacy/helpers/webAnalyticsConsent.helper'
import {
  WEB_ANALYTICS_CONSENT_DENIED,
  WEB_ANALYTICS_CONSENT_GRANTED,
} from '../privacy/privacyConsent.constants'

// PostHog: feature flags (server uses POSTHOG_API_KEY; client optional for future client flags).
let POSTHOG_KEY = ''
let POSTHOG_HOST = 'https://eu.i.posthog.com'
let posthogConfigSource = 'fallback'
try {
  const cfg = require('./posthog-config.generated')
  POSTHOG_KEY = cfg.POSTHOG_KEY ?? ''
  POSTHOG_HOST = cfg.POSTHOG_HOST ?? POSTHOG_HOST
  posthogConfigSource = POSTHOG_KEY ? 'posthog-config.generated' : 'generated-empty'
} catch {
  posthogConfigSource = 'file-missing'
}
if (!POSTHOG_KEY && typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_POSTHOG_KEY) {
  POSTHOG_KEY = process.env.EXPO_PUBLIC_POSTHOG_KEY
  POSTHOG_HOST = process.env.EXPO_PUBLIC_POSTHOG_HOST || POSTHOG_HOST
  posthogConfigSource = 'env-fallback'
}

// Mixpanel: events (client-only).
let MIXPANEL_TOKEN = ''
let mixpanelConfigSource = 'fallback'
try {
  const mixCfg = require('./mixpanel-config.generated')
  MIXPANEL_TOKEN = mixCfg.MIXPANEL_TOKEN ?? ''
  mixpanelConfigSource = MIXPANEL_TOKEN ? 'mixpanel-config.generated' : 'generated-empty'
} catch {
  mixpanelConfigSource = 'file-missing'
}
if (!MIXPANEL_TOKEN && typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_MIXPANEL_TOKEN) {
  MIXPANEL_TOKEN = process.env.EXPO_PUBLIC_MIXPANEL_TOKEN
  mixpanelConfigSource = 'env-fallback'
}

if (process.env.NODE_ENV !== 'production') {
  if (POSTHOG_KEY) logger.debug('[Analytics] PostHog configured', { posthogConfigSource, host: POSTHOG_HOST })
  if (MIXPANEL_TOKEN) logger.debug('[Analytics] Mixpanel configured', { mixpanelConfigSource })
  if (!MIXPANEL_TOKEN) logger.debug('[Analytics] Mixpanel token missing – set EXPO_PUBLIC_MIXPANEL_TOKEN or run inject-mixpanel-env.js; events will not be sent')
}
// When token is missing in production, warn once so devs see it in DevTools
if (!MIXPANEL_TOKEN && typeof window !== 'undefined' && !(window as unknown as { __zportiMixpanelWarned?: boolean }).__zportiMixpanelWarned) {
  ;(window as unknown as { __zportiMixpanelWarned?: boolean }).__zportiMixpanelWarned = true
  console.warn(
    '[Analytics] Mixpanel token not set in this build – events are not sent. Add EXPO_PUBLIC_MIXPANEL_TOKEN to your deploy (e.g. GitHub Actions secret) and run scripts/inject-mixpanel-env.js before building, then redeploy.'
  )
}

function buildIdentifyProperties(user: { firstName: string | null; lastName: string | null; username: string | null; primaryEmailAddress: { emailAddress: string } | null } | null): Record<string, string> {
  if (!user) return {}
  const props: Record<string, string> = {}
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username || null
  if (name) props.$name = name
  const email = user.primaryEmailAddress?.emailAddress
  if (email && !email.includes('@placeholder.com')) props.email = email
  props.environment = getAnalyticsEnvironment()
  return props
}

function AnalyticsIdentify() {
  const { userId, isSignedIn, user } = useSupabaseAuth()
  const { identify, reset } = useAnalytics()

  useEffect(() => {
    if (!isSignedIn) {
      reset()
      return
    }
    if (userId) {
      const props = buildIdentifyProperties(user)
      identify(userId, props)
    }
  }, [userId, isSignedIn, user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress, identify, reset])

  return null
}

function AnalyticsProviderInner({
  children,
  analyticsEnabled,
  analyticsReady,
  mixpanelReady,
}: {
  children: ReactNode
  analyticsEnabled: boolean
  analyticsReady: boolean
  mixpanelReady: boolean
}) {
  useEffect(() => {
    if (!analyticsEnabled || !analyticsReady) return
    if (Platform.OS === 'web') {
      // PostHog: do not call posthog-js init here — PostHogProvider already creates the client.
      // A second init doubled /e/, /flags/, and config traffic on web.
      if (MIXPANEL_TOKEN) initMixpanel(MIXPANEL_TOKEN)
    }
  }, [analyticsEnabled, analyticsReady])

  useEffect(() => {
    if (!analyticsEnabled || !mixpanelReady) return
    if (Platform.OS === 'web') return
    if (Platform.OS === 'ios' && IOS_DISABLED_NATIVE_SDKS.MIXPANEL) {
      void logLaunchPhase(LAUNCH_PHASE.MIXPANEL_INIT_SKIP, 'ios_disabled')
      return
    }
    if (!MIXPANEL_TOKEN) {
      void logLaunchPhase(LAUNCH_PHASE.MIXPANEL_INIT_SKIP, 'no_token')
      return
    }

    void (async () => {
      await logLaunchPhase(LAUNCH_PHASE.MIXPANEL_INIT_START)
      Mixpanel.init({ token: MIXPANEL_TOKEN, trackAutomaticEvents: false })
        .then(async () => {
          await logLaunchPhase(LAUNCH_PHASE.MIXPANEL_INIT_OK)
          logger.debug('[Analytics] Mixpanel (native) ready')
        })
        .catch((e: Error) => logger.warn('[Analytics] Mixpanel (native) init failed', { message: e.message }))
    })()
  }, [analyticsEnabled, mixpanelReady])

  return (
    <>
      {analyticsEnabled && mixpanelReady && MIXPANEL_TOKEN ? <AnalyticsIdentify /> : null}
      {children}
    </>
  )
}

function getPostHogAnalyticsShell(): typeof import('./PostHogAnalyticsShell').PostHogAnalyticsShell {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require('./PostHogAnalyticsShell').PostHogAnalyticsShell
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { bridgeReady, analyticsReady } = useNativeSdkInit()
  const [posthogReady, setPosthogReady] = useState(Platform.OS !== 'ios')
  const [mixpanelReady, setMixpanelReady] = useState(Platform.OS !== 'ios')
  const [isAnalyticsAllowed, setIsAnalyticsAllowed] = useState(() => {
    if (Platform.OS !== 'web') return getWebAnalyticsConsent() !== WEB_ANALYTICS_CONSENT_DENIED
    return getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_GRANTED
  })
  const posthogConfigured = !!POSTHOG_KEY
  const shouldEnableAnalytics = Platform.OS !== 'web' || isAnalyticsAllowed
  const inner = (
    <AnalyticsProviderInner
      analyticsEnabled={shouldEnableAnalytics}
      analyticsReady={analyticsReady}
      mixpanelReady={mixpanelReady}
    >
      {children}
    </AnalyticsProviderInner>
  )
  const posthogIosDisabled = Platform.OS === 'ios' && IOS_DISABLED_NATIVE_SDKS.POSTHOG
  const posthogEnabled =
    posthogConfigured && Platform.OS !== 'web' && posthogReady && !posthogIosDisabled

  useEffect(() => {
    if (!analyticsReady) {
      setPosthogReady(Platform.OS !== 'ios')
      return
    }
    if (Platform.OS !== 'ios') {
      setPosthogReady(true)
      return
    }
    const timer = setTimeout(() => setPosthogReady(true), NATIVE_SDK_POSTHOG_STAGGER_MS)
    return () => clearTimeout(timer)
  }, [analyticsReady])

  useEffect(() => {
    if (!posthogReady) {
      setMixpanelReady(Platform.OS !== 'ios')
      return
    }
    if (Platform.OS !== 'ios') {
      setMixpanelReady(true)
      return
    }
    const timer = setTimeout(() => setMixpanelReady(true), NATIVE_SDK_MIXPANEL_STAGGER_MS)
    return () => clearTimeout(timer)
  }, [posthogReady])

  useEffect(() => {
    if (!posthogEnabled) return
    void logLaunchPhase(LAUNCH_PHASE.POSTHOG_PROVIDER_MOUNT)
  }, [posthogEnabled])

  useEffect(() => {
    if (!bridgeReady) return
    void (async () => {
      await logLaunchPhase(LAUNCH_PHASE.ANALYTICS_CONSENT_HYDRATE_START)
      await hydrateNativeAnalyticsConsent()
      await logLaunchPhase(LAUNCH_PHASE.ANALYTICS_CONSENT_HYDRATE_OK)
    })()
    return subscribeAnalyticsConsent(() => {
      const consent = getWebAnalyticsConsent()
      if (Platform.OS === 'web') {
        setIsAnalyticsAllowed(consent === WEB_ANALYTICS_CONSENT_GRANTED)
        return
      }
      setIsAnalyticsAllowed(consent !== WEB_ANALYTICS_CONSENT_DENIED)
    })
  }, [bridgeReady])

  // Only mount PostHog SDK when we have a real key (avoids 404 on phc_disabled). When mounted, bridge client into context for pixels + feature flags.
  if (!posthogEnabled) {
    return (
      <PostHogClientContext.Provider value={null}>
        {inner}
      </PostHogClientContext.Provider>
    )
  }
  const PostHogAnalyticsShell = getPostHogAnalyticsShell()
  return (
    <PostHogAnalyticsShell apiKey={POSTHOG_KEY} host={POSTHOG_HOST}>
      {inner}
    </PostHogAnalyticsShell>
  )
}
